import {AmountDisplay, Button, DirectionalIcon, Divider, Typography} from '@sollapay/ui/components';
import {formatIsoDayMonth, formatTime} from '@sollapay/utils';
import {useTranslation} from 'react-i18next';

import {BreakdownRow} from './BreakdownRow';
import {BreakdownSection} from './BreakdownSection';
import {OutgoingSection} from './OutgoingSection';
import {useCountedLabel} from './useCountedLabel';

import type {BalanceBreakdown} from '@/features/trust-accounts/view/utils';
import type {Money} from '@sollapay/utils';

export interface BalanceBreakdownPanelProps {
  breakdown: BalanceBreakdown;
  updatedAt?: number;
  onViewAll: () => void;
}

export function BalanceBreakdownPanel({
  breakdown,
  updatedAt,
  onViewAll
}: BalanceBreakdownPanelProps) {
  const {t} = useTranslation('trustAccounts');

  return (
    <div data-slot="balance-breakdown-panel" className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <Typography weight="semibold">{t('view.balanceMetric.breakdown.title')}</Typography>
        {updatedAt ? (
          <Typography size="xs" color="tertiary" className="tabular-nums">
            {t('view.balanceMetric.breakdown.updatedAt', {time: formatTime(new Date(updatedAt))})}
          </Typography>
        ) : null}
      </div>

      <BreakdownRow
        icon="wallet"
        tone="neutral"
        title={t('view.balanceMetric.breakdown.confirmed')}
        subtitle={t('view.balanceMetric.breakdown.confirmedHint')}
        amount={
          <Typography dir="ltr" size="sm" weight="semibold" className="tabular-nums">
            {breakdown.confirmed.formatNIS({symbol: true})}
          </Typography>
        }
      />

      {!breakdown.incoming.amount.isZero() && <IncomingSection incoming={breakdown.incoming} />}
      {!breakdown.outgoing.total.amount.isZero() && (
        <>
          <OutgoingSection outgoing={breakdown.outgoing} />
          {/* Without outgoing money this equals the confirmed balance — it would just repeat it. */}
          <AvailableNowBox amount={breakdown.availableNow} />
        </>
      )}

      <Divider className="bg-transparent border-t border-dashed border-border" />

      <div className="flex items-center justify-between gap-4">
        <Typography weight="medium" color="secondary">
          {t('view.balanceMetric.breakdown.expected')}
        </Typography>
        <Typography dir="ltr" weight="bold" className="tabular-nums">
          {breakdown.expected.formatNIS({symbol: true})}
        </Typography>
      </div>

      <Typography size="xs" color="tertiary" className="whitespace-normal">
        {t('view.balanceMetric.breakdown.footnote')}
      </Typography>

      <Button
        variant="linkColor"
        className="h-auto self-start p-0"
        iconPosition="end"
        icon={<DirectionalIcon name="arrow-narrow-forward" className="size-4" />}
        onClick={onViewAll}
      >
        {t('view.balanceMetric.breakdown.viewAll')}
      </Button>
    </div>
  );
}

interface IncomingSectionProps {
  incoming: BalanceBreakdown['incoming'];
}

function IncomingSection({incoming}: IncomingSectionProps) {
  const {t} = useTranslation('trustAccounts');
  const countedLabel = useCountedLabel();

  return (
    <BreakdownSection label={t('view.balanceMetric.breakdown.incoming')}>
      <BreakdownRow
        icon="arrow-down"
        tone="success"
        title={countedLabel('deposits', incoming.count)}
        subtitle={
          incoming.expectedBy
            ? t('view.balanceMetric.breakdown.depositsExpected', {
                date: formatIsoDayMonth(incoming.expectedBy)
              })
            : undefined
        }
        amount={
          <AmountDisplay
            amount={incoming.amount.toString()}
            variant="credit"
            size="sm"
            className="tabular-nums"
          />
        }
      />
    </BreakdownSection>
  );
}

interface AvailableNowBoxProps {
  amount: Money;
}

function AvailableNowBox({amount}: AvailableNowBoxProps) {
  const {t} = useTranslation('trustAccounts');

  return (
    <div
      data-slot="balance-breakdown-available-now"
      className="flex items-center justify-between gap-4 rounded-lg bg-background-secondary p-4"
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <Typography weight="medium">{t('view.balanceMetric.breakdown.availableNow')}</Typography>
        <Typography size="xs" color="tertiary" className="whitespace-normal">
          {t('view.balanceMetric.breakdown.availableNowHint')}
        </Typography>
      </div>
      <Typography dir="ltr" weight="bold" className="shrink-0 tabular-nums">
        {amount.formatNIS({symbol: true})}
      </Typography>
    </div>
  );
}
